import { estimatePayoutQuery, myClaimsQuery } from "@/api/claims";
import { myLocationQueryOptions, payoutDetailsForMemberQuery, useMemberFeatures } from "@/api/me";
import { Badge } from "@/components/ui/Badge";
import { ColoredCard } from "@/components/ui/ColoredCard";
import { BoxLink, ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { TopAppBar } from "@/components/ui/TopAppBar";
import { describeAllowanceBalance, formatEapRepaymentCap, useMemberBenefits } from "@/hooks/useMemberBenefits";
import { AllowanceConversionNote } from "@/components/AllowanceConversionNote";
import { ClaimStatusBadge } from "@/pages/(app)/claims/ClaimStatusBadge";
import { YoureGettingPaidBox } from "@/pages/(app)/claims/YoureGettingPaidBox";
import { formatCents } from "@common/utils/i18n";
import { Box, Flex, Grid, Span, Text, UnstyledButton } from "@givenwell/components";
import { XIcon } from "@givenwell/core-solid-icons";
import { createLocalStorageState, useGlobalState } from "@givenwell/hooks";
import { Claim } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ComponentProps, useState } from "react";
import { RequestMoreSessionsButton } from "../eap/RequestMoreSessionsButton";
import { AddBankAccountBox } from "./AddBankAccountBox";
import { AllowanceClaimsHelp } from "./help/allowance/AllowanceClaimsFAQ";
import { EapClaimsHelp } from "./help/eap/EapClaimsFAQ";

export const Route = createFileRoute("/_auth/_app/claims/")({
  component: Page,
});

function Page() {
  const { data: claims } = useSuspenseQuery(myClaimsQuery());
  const { data: bank } = useSuspenseQuery(payoutDetailsForMemberQuery);
  const { data: payout } = useSuspenseQuery(estimatePayoutQuery);

  const hasPayouts = payout.cents > 0;
  const hasClaims = claims.length > 0;

  return (
    <Box
      css={{ height: "100%", width: "100%", maxW: 2100, mx: "auto", overflow: "hidden", px: "var(--layout-margin)" }}
    >
      <Grid
        css={{
          gridCols: 1,
          height: "100%",
          gap: "var(--layout-margin)",
          // "@md": {
          //   gridCols: 2,
          // },
        }}
      >
        <PageWrapper css={{ overflowY: "auto" }} topAppBar={<TopAppBar />}>
          <Box>
            <Box css={{ display: "none", "@md": { display: "block", pb: 16 } }}>
              <DesktopIntroCards />
            </Box>
            <Flex
              css={{
                alignItems: "center",
                mb: 16,
                "@lg": {
                  display: "none",
                },
              }}
            >
              <Text css={{ fontFamily: fonts.ftHabit, fontScale: "2xl" }}>Claims</Text>
              <Flex css={{ flex: "1 0 0px" }} />
              <Link to="/claims/help">
                <Span css={{ fontScale: "sm", fontFamily: fonts.ftHabit }}>How it works</Span>
              </Link>
            </Flex>

            {hasClaims && (
              <>
                {!bank.beneficiary_id ? (
                  <Box css={{ mb: 16 }}>
                    <AddBankAccountBox hasPayouts={hasPayouts} />
                  </Box>
                ) : hasPayouts ? (
                  <YoureGettingPaidBox estimate={payout} />
                ) : null}
              </>
            )}
            <Box css={{ pb: 16, "@lg": { display: "none" } }}>
              <MobileIntroCards />
            </Box>
            <ClaimsGSTWarning />
            {hasClaims ? (
              <>
                <Text
                  css={{
                    fontWeight: 600,
                    fontFamily: fonts.ftHabit,
                    fontScale: "2xl",
                    pb: 8,
                  }}
                >
                  Your Claims
                </Text>
                <Grid
                  css={{
                    gap: 16,
                    gridCols: 1,
                  }}
                >
                  {claims.map(claim => (
                    <ClaimCard key={claim.claim_id} claim={claim} />
                  ))}
                </Grid>
              </>
            ) : (
              <EmptyState />
            )}
          </Box>
        </PageWrapper>
      </Grid>
    </Box>
  );
}

function DesktopIntroCards() {
  const { hasAllowance, hasEap } = useMemberFeatures();
  const { eap, allowance } = useMemberBenefits();
  const { balance, conversionNote, conversionHelp } = describeAllowanceBalance(allowance);
  const showEapClaims = hasEap && !!eap?.canSubmitClaims;

  return (
    <Flex css={{ gap: 16 }}>
      {showEapClaims && (
        <Card css={{ backgroundColor: colors.green, flex: "1 0 0px" }}>
          <Blob fill={colors.greenAccent} />
          <Overline>Funded Sessions:</Overline>
          <Title>
            {(eap?.availableSessions ?? 0) > 0 ? (
              <>
                You have <Highlight>{eap?.availableSessions} sessions</Highlight> available to claim.
              </>
            ) : (
              <>
                You have <Highlight>no sessions</Highlight> remaining.
              </>
            )}
          </Title>
          <Text>
            Upload a receipt from a session with a wellbeing provider of your choice, and we'll repay you up to{" "}
            {formatEapRepaymentCap(eap)} per session into your bank account.
          </Text>
          <Buttons>
            {(eap?.availableSessions ?? 0) > 0 ? (
              <ButtonLink
                variant="filled"
                color="pink"
                to="/claims/new"
                search={{ eap_supporter_id: eap?.supporterId }}
              >
                Claim back for a session
              </ButtonLink>
            ) : (
              <RequestMoreSessionsButton />
            )}
            <ButtonLink variant="filled" color="neutralLight" to="/claims/policy/eap">
              See what you can claim
            </ButtonLink>
          </Buttons>
        </Card>
      )}
      {hasAllowance && (
        <Card css={{ backgroundColor: colors.cyan, flex: "1 0 0px" }}>
          <Blob fill={colors.cyanAccent} />
          <Overline>Wellbeing Allowance:</Overline>
          <Box css={{ spaceY: 8 }}>
            <Title>
              You have <Highlight>{balance}</Highlight> available to claim.
            </Title>
            {conversionNote && conversionHelp && (
              <AllowanceConversionNote note={conversionNote} help={conversionHelp} />
            )}
          </Box>
          <Text>
            Upload a receipt for something you've spent on for your wellbeing, and we'll pay you back into your bank
            account.
          </Text>
          <Buttons>
            <ButtonLink variant="filled" color="pink" to="/claims/new">
              Create new claim
            </ButtonLink>
            <ButtonLink variant="filled" color="neutralLight" to="/claims/policy/allowance">
              See what you can claim
            </ButtonLink>
          </Buttons>
        </Card>
      )}
    </Flex>
  );
}

const Card = styled("div", {
  padding: 16,
  backgroundColor: colors.green,
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 12,
  overflow: "hidden",
  position: "relative",
  "&>*": {
    position: "relative",
  },
});

const Overline = styled("div", {
  fontScale: "xl",
  weight: 600,
  fontFamily: fonts.ftHabit,
});

const Title = styled("div", {
  fontScale: "3xl",
  lineHeight: 1.1,
  weight: 600,
  fontFamily: fonts.ftHabit,
  textWrap: "pretty",
});

const Highlight = styled("span", {
  backgroundColor: colors.white,
  borderRadius: 6,
  px: 4,
});

const Buttons = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  maxWidth: 300,
  width: "100%",
  mx: "auto",
});

function Blob({ fill, ...props }: ComponentProps<"svg"> & { fill: string }) {
  return (
    <svg
      width="412"
      height="723"
      viewBox="0 0 412 723"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        ...props.style,
      }}
    >
      <path
        d="M-493.801 155.054C-344.986 -41.7923 19.7266 -90.438 196.728 243.936C356.498 545.745 497.183 848.792 348.389 1045.61C199.594 1242.43 45.3354 1082.9 -238.815 927.212C-511.119 778.045 -642.616 351.9 -493.801 155.054Z"
        fill={fill}
      />
    </svg>
  );
}

function EmptyState() {
  const { hasAllowance, hasEap } = useMemberFeatures();
  const { eap } = useMemberBenefits();
  const showEapClaims = hasEap && !!eap?.canSubmitClaims;
  const [showEap, setShowEap] = useState(false);
  if (hasAllowance && showEapClaims) {
    return (
      <Box css={{ width: "100%", maxWidth: 1000, mx: "auto", mt: 0 }}>
        <Flex css={{ items: "center", mb: 16 }}>
          <Text css={{ fontScale: "xl", fontFamily: fonts.ftHabit, flex: "1 0 0px" }}>How it works</Text>
          <Flex css={{ justifyContent: "center", gap: 16 }}>
            <NavTab className={!showEap ? "active" : ""} onClick={() => setShowEap(false)}>
              Allowances
            </NavTab>
            <NavTab className={showEap ? "active" : ""} onClick={() => setShowEap(true)}>
              Free Sessions
            </NavTab>
          </Flex>
        </Flex>
        <Flex
          css={{
            flexDirection: "column",
            gap: 16,
          }}
        >
          {showEap ? <EapClaimsHelp /> : <AllowanceClaimsHelp />}
        </Flex>
      </Box>
    );
  }
  return (
    <Flex
      css={{
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Text css={{ fontFamily: fonts.ftHabit, fontScale: "lg" }}>How it works</Text>
      {!hasAllowance && showEapClaims ? <EapClaimsHelp /> : <AllowanceClaimsHelp />}
    </Flex>
  );
}
const NavTab = styled("button", {
  display: "flex",
  height: 40,
  items: "center",
  fontWeight: 500,

  borderBottom: `3px solid transparent`,
  "&.active": {
    borderBottomColor: colors.pink,
  },
});

function MobileIntroCards() {
  const { hasEap, hasAllowance } = useMemberFeatures();
  const { allowance, eap } = useMemberBenefits();
  const { balance, conversionNote, conversionHelp } = describeAllowanceBalance(allowance);
  const showEapClaims = hasEap && !!eap?.canSubmitClaims;

  const claims = useQuery(myClaimsQuery());
  const hasClaims = claims.data?.length;

  if (hasClaims && hasAllowance && showEapClaims) {
    // Show reduced-height combined intro
    return (
      <ColoredCard color="green">
        <Box css={{ spaceY: 16 }}>
          <Text css={{ fontFamily: fonts.ftHabit, fontScale: "xl", weight: 600 }}>Your remaining balances</Text>
          <Flex css={{ gap: 16 }}>
            <Box
              css={{
                backgroundColor: colors.cream,
                flex: "1 0 0px",
                textAlign: "center",
                borderRadius: 6,
                padding: 8,
              }}
            >
              <Text css={{ fontFamily: fonts.ftHabit, fontScale: "xl" }}>{eap?.availableSessions}</Text>
              <Text css={{ fontFamily: fonts.ftHabit, fontScale: "xs" }}>Free Sessions</Text>
            </Box>
            <Box
              css={{
                backgroundColor: colors.cream,
                flex: "1 0 0px",
                textAlign: "center",
                borderRadius: 6,
                padding: 8,
              }}
            >
              <Text css={{ fontFamily: fonts.ftHabit, fontScale: "xl" }}>{balance}</Text>
              <Text css={{ fontFamily: fonts.ftHabit, fontScale: "xs" }}>Wellbeing Allowance</Text>
            </Box>
          </Flex>
          <ButtonLink variant="filled" color="pink" to="/claims/new" css={{ width: "100%" }}>
            Create a new claim
          </ButtonLink>
        </Box>
      </ColoredCard>
    );
  }

  return (
    <Flex css={{ flexDirection: "column", gap: 16 }}>
      {hasAllowance && (
        <ColoredCard color="green">
          <Box css={{ flex: "1 0 0px", spaceY: 12 }}>
            <Box css={{ spaceY: 6 }}>
              <Text
                css={{ fontFamily: fonts.ftHabit, weight: 600, fontScale: "lg", textWrap: "pretty", lineHeight: 1.2 }}
              >
                You have {balance} to spend on wellbeing support.
              </Text>
              {conversionNote && conversionHelp && (
                <AllowanceConversionNote note={conversionNote} help={conversionHelp} />
              )}
            </Box>
            <Text css={{ fontScale: "sm", textWrap: "pretty" }}>
              Upload a receipt for something that you've purchased for your wellbeing, and we'll repay you into your
              bank account.
            </Text>
            <ButtonLink
              variant="filled"
              color="pink"
              to="/claims/new"
              search={{ eap_supporter_id: eap?.supporterId }}
              css={{ width: "100%" }}
            >
              Create a new claim
            </ButtonLink>
            <Text css={{ fontScale: "sm", textAlign: "center" }}>
              <Link to="/claims/policy/allowance">
                <Span css={{ textDecoration: "underline" }}>See what you can claim</Span>
              </Link>
            </Text>
          </Box>
        </ColoredCard>
      )}
      {showEapClaims && (
        <ColoredCard color="cyan">
          <Box css={{ flex: "1 0 0px", spaceY: 12 }}>
            <Text
              css={{ fontFamily: fonts.ftHabit, weight: 600, fontScale: "lg", textWrap: "pretty", lineHeight: 1.2 }}
            >
              {(eap?.availableSessions ?? 0) > 0
                ? `You have ${eap?.availableSessions} sessions available to see a wellbeing provider.`
                : "You have no free sessions remaining."}
            </Text>
            <Text css={{ fontScale: "sm" }}>
              Upload a receipt from a session with a wellbeing provider of your choice, and we'll repay you up to{" "}
              {formatEapRepaymentCap(eap)} per session into your bank account.
            </Text>
            {(eap?.availableSessions ?? 0) > 0 ? (
              <ButtonLink
                variant="filled"
                color="pink"
                to="/claims/new"
                search={{ eap_supporter_id: eap?.supporterId }}
                css={{ width: "100%" }}
              >
                Create a new claim
              </ButtonLink>
            ) : (
              <RequestMoreSessionsButton />
            )}
            <Text css={{ fontScale: "sm", textAlign: "center" }}>
              <Link to="/claims/policy/eap">
                <Span css={{ textDecoration: "underline" }}>See list of approved provider types</Span>
              </Link>
            </Text>
          </Box>
        </ColoredCard>
      )}
    </Flex>
  );
}

function ClaimCard({ claim }: { claim: Claim }) {
  const isEmployerFunded = claim.funding_source_id === "employer_funded";
  const hasUnpaidTokens = !isEmployerFunded && claim.paid_amount_cents != claim.approved_amount_cents;
  return (
    <BoxLink
      to="/claims/$claimId"
      params={{
        claimId: claim.claim_id,
      }}
      css={{
        backgroundColor: colors.warmGreyHalf,
        px: 16,
        py: 16,
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flex>
        <Box css={{ flex: "1 0 0px" }}>
          <Text css={{ fontScale: "md", color: colors.text, weight: 500 }}>{claim.vendor_name}</Text>
          <Text css={{ fontScale: "sm", color: colors.text2, weight: 500 }}>
            {new Date(claim.doc_date).toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: new Date(claim.doc_date).getFullYear() === new Date().getFullYear() ? undefined : "numeric",
            })}
          </Text>
          {isEmployerFunded && (
            <Badge color="teal" css={{ mt: 4, rounded: 4, h: 18, px: 6 }}>
              Employer Funded
            </Badge>
          )}
        </Box>
        <Flex
          css={{
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Text>{formatCents(claim.approved_amount_cents, claim.doc_currency)}</Text>
          <ClaimStatusBadge status={claim.status} hasUnpaidTokens={hasUnpaidTokens} />
        </Flex>
      </Flex>
    </BoxLink>
  );
}

const WARNING_DISMISS_KEY = "claims-gst-warning-dismissed";
const state = createLocalStorageState(WARNING_DISMISS_KEY, false);

function ClaimsGSTWarning() {
  const [dismissed, setDismissed] = useGlobalState(state);

  const { data: location } = useQuery(myLocationQueryOptions);

  const isAU = location?.country_code === "AU";

  if (dismissed || !isAU) {
    return null;
  }

  const bannerText = "Please ensure your receipt includes the business' ABN number";

  return (
    <Box css={{ display: "flex", justifyContent: "center", mb: 20 }}>
      <Box
        css={{
          backgroundColor: colors.cyan,
          borderRadius: 16,
          minHeight: 32,
          items: "center",
          display: "flex",
          pl: 4,
          py: 4,
        }}
      >
        <Span css={{ ml: 12, mr: 8, fontScale: "sm" }}>{bannerText}</Span>
        <UnstyledButton
          css={{
            backgroundColor: colors.cyanLight,
            size: 32 - 8,
            flexShrink: 0,
            mr: 4,
            display: "flex",
            items: "center",
            justifyContent: "center",
            borderRadius: 9999,
          }}
          onClick={() => setDismissed(true)}
        >
          <XIcon width={12} height={12} />
        </UnstyledButton>
      </Box>
    </Box>
  );
}
